/**
 * TraceTimeline — Sub-PR H (issue #516)
 *
 * Renders the chronological per-order event chain returned by
 * `GET /api/orders/:shopifyOrderId/trace` (commerce order-routes,
 * ADR-0012 §Per-order debugging tool).
 *
 * Six event kinds (closed enum mirror from `services/orders.ts`):
 *   - webhook_received — Shopify/Onfleet webhook ingressed
 *   - order_projected  — the `orders` row's projection write
 *   - webhook_failure  — a `webhook_failures` row recorded
 *   - operator_action  — an audited per-order operator route
 *   - fulfillment_job  — one per (incl. soft-deleted) `fulfillment_jobs` row
 *   - onfleet_task     — a job whose Onfleet task id is set (dispatch step)
 *
 * Presentation lives in ONE config table (`EVENT_PRESENTATION`)
 * indexed by `TraceEventKind` (R1 P2 — collapsed the earlier twin
 * `configFor` + `summaryFor` switches). Two contracts here, separated
 * because they live at different layers:
 *
 *   COMPILE-TIME — `Record<TraceEventKind, EventPresentation>` makes
 *     the table total over the closed enum. Adding a kind to
 *     `TRACE_EVENT_KINDS` without a corresponding table entry
 *     fails the build at this file — the operator-visible
 *     rendering site is where the missing-case noise lands, not
 *     somewhere deep downstream (EQD §1).
 *
 *   RUNTIME — `presentationFor(kind: string)` admits an unknown
 *     `kind` (backend can ship a new variant before the frontend
 *     deploys; the per-order debug view is operator-better-not-
 *     worse — we render the event with a generic card rather than
 *     500'ing the whole admin UI). The `KNOWN_KINDS` Set + cast
 *     is the honest expression of "compile says total, runtime
 *     admits drift." R2 P1 collapsed the earlier
 *     `EVENT_PRESENTATION[kind] ?? fallback` form — that form lied
 *     because the `??` was structurally dead under the total
 *     `Record` annotation, even though the runtime fallback was
 *     load-bearing.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Package,
  ServerCog,
  Truck,
  UserCog,
  Webhook,
} from 'lucide-react';
import { Badge, EntityLink, ExternalServiceLink } from '@resource-kit/ui/react';
import { cn } from '@resource-kit/ui';
import { shortenId } from '@resource-kit/controllers';
import { JsonPanel } from '@components/ui/JsonPanel';
import { closedEnumLookup } from '@lib/closedEnumGuard';
import { formatDateTime } from '@lib/datetime';
import { EXTERNAL_URLS } from '@/constants/config';
import i18n from '@/i18n';
import { type TraceEvent, type TraceEventKind, TRACE_EVENT_KINDS } from '@/services/orders';
import type { FulfillmentJob } from '@/services';
import { useOrderTrace } from '../hooks/useOrderTrace';
import { jobDisplayName } from '../jobDisplay';

/**
 * Resolve a job UUID to the operator-facing display name (`#1016-0713`
 * form). `null` when the id is not in the fetched job set — the caller
 * then falls back to a truncated UUID rather than leaking the raw 36-char
 * string into the link text (#1202).
 */
type JobNameResolver = (jobId: string) => string;

/**
 * Shortened-id fallback when a job id can't be resolved (#1202). Link
 * text is a string, so this goes through the shared shortener rather
 * than `IdCell` — job ids are UUIDs, so what survives is the trailing
 * group, not the timestamp prefix (#1978). The 8-char budget only
 * applies to ids that are not UUID-shaped.
 */
function truncateJobId(jobId: string): string {
  return shortenId(jobId, 8).text;
}

interface TraceTimelineProps {
  shopifyOrderId: string;
  /** When false, the panel renders nothing — used to gate on admin role. */
  enabled?: boolean;
  /**
   * The order's fulfillment jobs, from the SAME query the order structure
   * tree (OrderDetailExtra) already populates — passed down as a prop
   * rather than re-fetched here (#1202 review polish: the earlier
   * duplicate `useQuery` in this file used `staleTime: 0` while the
   * parent's used 60s, so the two diverged and the panel silently
   * background-refetched on every mount).
   */
  jobs?: FulfillmentJob[];
}

interface EventPresentation {
  Icon: typeof Webhook;
  badgeVariant: 'default' | 'secondary' | 'destructive' | 'outline';
  labelKey: string;
  /** Tailwind classes for the rail dot background + icon color. */
  dotClass: string;
  /**
   * One-line summary derived from the row's `detail` shape. `kind` is
   * threaded through so the UNKNOWN fallback (R3 P1) can surface the
   * raw kind string — without it, an operator scanning a timeline at
   * 3am sees a blank-summary card and silently mis-debugs.
   */
  summary: (detail: Record<string, unknown>, kind: string) => string;
  /**
   * Optional inline entity / external links rendered under the summary
   * (e.g. a fulfillment_job → its `/dispatch/jobs/{id}` detail page, an
   * onfleet_task → job + Onfleet dashboard). `null` when a row has no
   * navigable target. `resolveJobName` maps a job UUID to its display
   * name (`#1016-0713`) so the visible link text is operator-legible,
   * not a raw UUID (#1202).
   */
  renderLinks?: (detail: Record<string, unknown>, resolveJobName: JobNameResolver) => ReactNode;
  /**
   * When true, a non-null `detail.deletedAt` marks the step cancelled
   * (a soft-deleted / rebuilt job — surfaced by the backend #1186, not
   * dropped) with a destructive badge next to the kind label.
   */
  cancellable?: boolean;
}

/**
 * The single `detail` key under which a `webhook_received` event nests
 * its decoded wire body — `{ text, truncated }` (#1203). Held as a const
 * so the metadata-table omit and the payload read agree on ONE name (the
 * front/back contract is still runtime, but the frontend reads it from a
 * single source).
 */
const PAYLOAD_DETAIL_KEY = 'rawPayload' as const;

const EMPTY = '';

/** Read a string field off an untrusted `detail` blob, else `''`. */
function detailStr(detail: Record<string, unknown>, key: string): string {
  const value = detail[key];
  return typeof value === 'string' ? value : EMPTY;
}

/**
 * Metadata table for a webhook_received event: the detail minus the
 * fields that drive the dedicated raw-payload table (#1203), so the
 * body is not also dumped as a giant one-line string alongside it.
 */
function omitPayloadKeys(detail: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(detail).filter(([k]) => k !== PAYLOAD_DETAIL_KEY));
}

/**
 * Per-kind presentation config. The `Record<TraceEventKind, …>`
 * annotation pins exhaustiveness — adding a new kind to
 * `TRACE_EVENT_KINDS` without extending this table fails the build
 * exactly here, surfacing the unhandled case at the operator-visible
 * rendering site (EQD §1).
 */
const EVENT_PRESENTATION: Record<TraceEventKind, EventPresentation> = {
  [TRACE_EVENT_KINDS.WEBHOOK_RECEIVED]: {
    Icon: Webhook,
    badgeVariant: 'default',
    labelKey: 'order.trace.kind.webhookReceived',
    dotClass: 'bg-primary/10 text-primary',
    summary: (detail) =>
      [detailStr(detail, 'provider'), detailStr(detail, 'topic')].filter(Boolean).join(' · '),
  },
  [TRACE_EVENT_KINDS.ORDER_PROJECTED]: {
    Icon: ServerCog,
    badgeVariant: 'secondary',
    labelKey: 'order.trace.kind.orderProjected',
    dotClass: 'bg-muted text-muted-foreground',
    summary: (detail) =>
      [detailStr(detail, 'source'), detailStr(detail, 'financialStatus')]
        .filter(Boolean)
        .join(' · '),
  },
  [TRACE_EVENT_KINDS.WEBHOOK_FAILURE]: {
    Icon: AlertTriangle,
    badgeVariant: 'destructive',
    labelKey: 'order.trace.kind.webhookFailure',
    dotClass: 'bg-destructive/10 text-destructive',
    summary: (detail) =>
      [detailStr(detail, 'errorKind'), detailStr(detail, 'errorMessageHead')]
        .filter(Boolean)
        .join(': '),
  },
  [TRACE_EVENT_KINDS.OPERATOR_ACTION]: {
    Icon: UserCog,
    badgeVariant: 'outline',
    labelKey: 'order.trace.kind.operatorAction',
    dotClass: 'bg-muted text-muted-foreground',
    // audit_logs row: HTTP method + the matched route template + the
    // gate outcome (allow / deny) — the operator's own action on the
    // order (refetch, …).
    summary: (detail) =>
      [
        [detailStr(detail, 'method'), detailStr(detail, 'routePath')].filter(Boolean).join(' '),
        detailStr(detail, 'outcome'),
      ]
        .filter(Boolean)
        .join(' · '),
  },
  [TRACE_EVENT_KINDS.FULFILLMENT_JOB]: {
    Icon: Package,
    badgeVariant: 'secondary',
    labelKey: 'order.trace.kind.fulfillmentJob',
    dotClass: 'bg-muted text-muted-foreground',
    cancellable: true,
    summary: (detail) =>
      [detailStr(detail, 'status'), detailStr(detail, 'shippingDate')].filter(Boolean).join(' · '),
    renderLinks: (detail, resolveJobName) => {
      const jobId = detailStr(detail, 'id');
      if (jobId.length === 0) return null;
      return (
        <EntityLink to={`/dispatch/jobs/${jobId}`} className="text-xs font-mono">
          {resolveJobName(jobId)}
        </EntityLink>
      );
    },
  },
  [TRACE_EVENT_KINDS.ONFLEET_TASK]: {
    Icon: Truck,
    badgeVariant: 'secondary',
    labelKey: 'order.trace.kind.onfleetTask',
    dotClass: 'bg-muted text-muted-foreground',
    cancellable: true,
    summary: (detail) => detailStr(detail, 'driverId'),
    // Onfleet-task step: link back to the owning job's detail page
    // (`/dispatch/jobs/{jobId}` — the `/tasks/*` route retired, #1187)
    // plus an Onfleet dashboard external link for the task itself.
    renderLinks: (detail, resolveJobName) => {
      const jobId = detailStr(detail, 'jobId');
      const taskId = detailStr(detail, 'onfleetTaskId');
      if (jobId.length === 0 && taskId.length === 0) return null;
      return (
        <span className="inline-flex flex-wrap items-center gap-3">
          {jobId.length > 0 && (
            <EntityLink to={`/dispatch/jobs/${jobId}`} className="text-xs font-mono">
              {resolveJobName(jobId)}
            </EntityLink>
          )}
          {taskId.length > 0 && (
            <ExternalServiceLink
              href={EXTERNAL_URLS.onfleetTask(taskId)}
              label="Onfleet"
              ariaLabel={i18n.t('ui.openInOnfleet')}
            />
          )}
        </span>
      );
    },
  },
};

/**
 * Module-level fallback used for any `event.kind` not in the closed
 * enum (R2 P2 — hoisted out of the inline-per-render object literal
 * the R1 fix produced). Reached when the backend ships a new kind
 * before the frontend re-deploys; the per-order trace is
 * operator-better-not-worse, so we render an unlabelled card rather
 * than crashing the panel.
 */
const UNKNOWN_KIND_PRESENTATION: EventPresentation = {
  Icon: HelpCircle,
  badgeVariant: 'outline',
  labelKey: 'order.trace.kind.unknown',
  dotClass: 'bg-muted text-muted-foreground',
  // R3 P1 fix: surface the raw kind string. The previous empty
  // summary made unknown events visually identical to a known
  // event with no derivable summary — operators scanning the
  // timeline at 3am would skip the card. The raw kind here is
  // operator-greppable (they can paste it into the backend repo
  // search to find the writer).
  summary: (_detail, kind) => (kind.length > 0 ? `raw kind: ${kind}` : ''),
};

/**
 * Look up the presentation for an `event.kind`. R2 Sub-PR F
 * extracted the COMPILE-TIME `Record` + RUNTIME `KNOWN_KINDS` Set
 * pattern into `src/lib/closedEnumGuard.ts::closedEnumLookup` so this
 * file + `ReviewQueuePage.tsx` no longer carry hand-rolled
 * duplicates. The helper preserves both invariants: adding a kind
 * to `TRACE_EVENT_KINDS` without an `EVENT_PRESENTATION` entry
 * fails the build (the `Record<TraceEventKind, …>` annotation
 * still pins exhaustiveness); unknown runtime kinds flow to
 * `UNKNOWN_KIND_PRESENTATION` instead of crashing.
 */
const presentationFor = closedEnumLookup<TraceEventKind, EventPresentation>(
  EVENT_PRESENTATION,
  UNKNOWN_KIND_PRESENTATION,
);

interface TraceRowProps {
  event: TraceEvent;
  showRail: boolean;
  resolveJobName: JobNameResolver;
}

/** One row of the timeline. See `presentationFor` for kind handling. */
function TraceRow({ event, showRail, resolveJobName }: TraceRowProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const cfg = presentationFor(event.kind);
  const summary = cfg.summary(event.detail, event.kind);
  const links = cfg.renderLinks?.(event.detail, resolveJobName) ?? null;
  // A soft-deleted / rebuilt job (backend #1186 surfaces tombstoned rows
  // rather than dropping them) carries a non-null `deletedAt` — mark the
  // step cancelled so an operator does not misread a reworked order.
  const isCancelled = cfg.cancellable === true && event.detail.deletedAt != null;
  // #1203: a `webhook_received` event's original JSON body is fed to a
  // dedicated JsonKeyValueTable (it self-parses the string). Split it out
  // of the metadata table and surface the backend's `truncated` flag.
  // #1203: the webhook body now travels nested under the single
  // `PAYLOAD_DETAIL_KEY` key — `{ text, truncated }` — so the truncation
  // flag stays with the body it describes. Narrow the untrusted detail.
  const payloadDetail = event.detail[PAYLOAD_DETAIL_KEY];
  const rawPayload =
    typeof payloadDetail === 'object' &&
    payloadDetail !== null &&
    typeof (payloadDetail as { text?: unknown }).text === 'string'
      ? (payloadDetail as { text: string }).text
      : null;
  const isTruncated =
    typeof payloadDetail === 'object' &&
    payloadDetail !== null &&
    (payloadDetail as { truncated?: unknown }).truncated === true;
  const meta = rawPayload !== null ? omitPayloadKeys(event.detail) : event.detail;
  // R3 P2 accessibility fix: link the disclosure button to its
  // `<pre>` target so screen readers / browser dev-tools surface
  // the relationship. Same kind-namespaced shape as `rowKey` so a
  // page with N TraceRows never collides ids across panels.
  const detailId = `trace-event-detail-${event.kind}-${
    typeof event.detail.id === 'string' || typeof event.detail.id === 'number'
      ? event.detail.id
      : event.at
  }`;

  return (
    <li className="relative pl-9">
      {/* Vertical rail */}
      {showRail && <span aria-hidden className="absolute left-3 top-7 bottom-0 w-px bg-border" />}
      {/* Kind dot */}
      <span
        aria-hidden
        className={cn(
          'absolute left-0 top-0 flex h-6 w-6 items-center justify-center rounded-full',
          cfg.dotClass,
        )}
      >
        <cfg.Icon className="h-3.5 w-3.5" />
      </span>
      <div className="space-y-1 pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={cfg.badgeVariant} className="text-[10px] uppercase">
            {t(cfg.labelKey, { defaultValue: event.kind })}
          </Badge>
          {isCancelled && (
            <Badge variant="destructive" className="text-[10px] uppercase">
              {t('order.trace.cancelled')}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">{formatDateTime(event.at)}</span>
        </div>
        {summary.length > 0 && <p className="text-sm text-foreground break-words">{summary}</p>}
        {links && <div className="flex flex-wrap items-center gap-3">{links}</div>}
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          aria-expanded={open}
          aria-controls={detailId}
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {open ? t('order.trace.hideDetails') : t('order.trace.showDetails')}
        </button>
        {open && (
          // R1 P1 fix: bound vertical height. A `webhook_received`
          // event carries the original Shopify JSON body (#1203), which
          // can be multi-KB, so `max-h-64` + `overflow-auto` scrolls
          // inside the card. The body is rendered with `JsonPanel`
          // (a `JsonKeyValueTable` plus copy/expand affordances — the
          // same indented key/value view as the webhook-event detail
          // page); it self-parses the JSON-string `rawPayload`, so no
          // manual parse/pretty-print here. The expand button opens a
          // near-fullscreen dialog for the multi-KB bodies.
          <div
            id={detailId}
            className="mt-1 max-h-64 overflow-auto rounded-md border bg-surface-1 p-2"
          >
            {isTruncated && (
              <p className="mb-1.5 text-[11px] text-warning">{t('order.trace.payloadTruncated')}</p>
            )}
            <JsonPanel value={meta} ariaLabel={t(cfg.labelKey, { defaultValue: event.kind })} />
            {rawPayload !== null && (
              <JsonPanel
                className="mt-2"
                value={rawPayload}
                label={t('order.trace.payloadLabel')}
                ariaLabel={t('order.trace.payloadLabel')}
              />
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Derive a stable row key from `detail.id` when present.
 *
 * WHY `detail.id` IS A STRING WHEREVER IT EXISTS. Four of the six event kinds
 * `backend/src/contexts/commerce/app/order-trace.ts::queryOrderTrace` emits
 * carry an `id`, and each is a row's own primary key:
 *
 *   WEBHOOK_RECEIVED  a `webhook_events` projection, `id` among its columns
 *   WEBHOOK_FAILURE   a `webhook_failures` projection, likewise
 *   OPERATOR_ACTION   an `audit_logs` projection, likewise
 *   FULFILLMENT_JOB   a projection whose first key is `id: j.id`
 *
 * All four are explicit column lists — none of them is a whole row, and the
 * only thing that matters about them here is that each one selects `id`.
 *
 * Those four tables declare `id: uuidPrimaryKey()` —
 * `backend/src/platform/webhook/store/schema.ts::webhookEvents` and
 * `backend/src/platform/webhook/store/schema.ts::webhookFailures`,
 * `backend/src/platform/audit/store/schema.ts::auditLogs`,
 * `backend/src/contexts/delivery/store/schema.ts::fulfillmentJobs` — which is
 * TEXT. The other two kinds, ORDER_PROJECTED and ONFLEET_TASK, project named
 * keys (`orderId`, `jobId`) and carry no `id` at all, so they take the
 * composite path below.
 *
 * So no backend in place today can put a number in `detail.id`, and the
 * numeric branch is unreachable. Note the scope of that: `detail` arrives as
 * unvalidated JSON over the wire, so this is a statement about the current
 * reader, not a guarantee about this function's input.
 *
 * An earlier version of this paragraph rested on a survey of the backend that
 * was false, and cited a route that existed only in the archived backend
 * (now only in tag `archive-snapshot-2026-10`) — which
 * is why the citation rule reports an archived target as a finding (#2269).
 *
 * Falls back to a composite when an event lacks `id` — same-kind
 * same-`at` events (e.g. a webhook + its order_upserted within
 * the same ms) would otherwise collide on
 * `${kind}-${at}-${index}` after a sort, and React's `useState
 * open/close` would leak between rows.
 */
function rowKey(event: TraceEvent, index: number): string {
  const id = event.detail.id;
  // `:${at}` is redundant today and is kept anyway. Every branch below starts
  // with `${event.kind}`, so two different kinds can never collide however
  // they were derived, and within one kind an `id` is a row's primary key —
  // a collision would need the same row emitted twice under the same kind. No
  // reader does that, so there is no scenario to point at: this is defensive
  // redundancy, not a guard against something known. It costs one string
  // concatenation, and a leaked `useState open/close` between rows is the
  // failure it would prevent.
  //
  // The two `id` branches return the identical template. Merging them, and
  // deleting the numeric one as unreachable, is #2325 — a production change
  // this comment-only branch does not make.
  if (typeof id === 'string' && id.length > 0) {
    return `${event.kind}:${id}:${event.at}`;
  }
  if (typeof id === 'number') {
    return `${event.kind}:${id}:${event.at}`;
  }
  return `${event.kind}:${event.at}:${index}`;
}

export function TraceTimeline({ shopifyOrderId, enabled = true, jobs = [] }: TraceTimelineProps) {
  const { t } = useTranslation();
  const query = useOrderTrace(shopifyOrderId, { enabled });

  // Maps a job UUID → its server-computed display name so the
  // fulfillment_job / onfleet_task link text reads `#1016-0713` instead
  // of a raw UUID (#1202). `jobs` comes from the parent (OrderDetailExtra)
  // rather than a query fired here — see the `jobs` prop doc.
  const jobNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const job of jobs) {
      map.set(job.id, jobDisplayName(job));
    }
    return map;
  }, [jobs]);
  const resolveJobName = useCallback<JobNameResolver>(
    (jobId) => jobNameById.get(jobId) ?? truncateJobId(jobId),
    [jobNameById],
  );

  if (!enabled) return null;

  if (query.isLoading) {
    return <div className="text-xs text-muted-foreground">{t('order.trace.loading')}</div>;
  }

  if (query.isError) {
    return (
      <div className="text-xs text-destructive">
        {t('order.trace.errorLoad', { detail: query.error.message })}
      </div>
    );
  }

  const data = query.data;
  if (!data) {
    return null;
  }

  if (data.kind === 'order_not_found') {
    return <div className="text-xs text-muted-foreground">{t('order.trace.notFound')}</div>;
  }

  if (data.events.length === 0) {
    return <div className="text-xs text-muted-foreground">{t('order.trace.empty')}</div>;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {t('order.trace.summary', {
          events: data.events.length,
          traces: data.traceIds.length,
        })}
      </p>
      <ol className="relative space-y-0">
        {data.events.map((event, index) => (
          <TraceRow
            key={rowKey(event, index)}
            event={event}
            showRail={index < data.events.length - 1}
            resolveJobName={resolveJobName}
          />
        ))}
      </ol>
    </div>
  );
}
